import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { XCircle, Loader } from "lucide-react";
import Api from "../Api/Api.json";

export default function PaymentFailure() {
  const [isLoading, setIsLoading] = useState(true);
  const [orderId, setOrderId] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState('');
  const navigate = useNavigate();

  // Determine environment based on URL
  const getEnvironment = () => {
    const hostname = window.location.hostname;
    // Check if it's localhost or 127.0.0.1 (development)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'development';
    }
    // Otherwise it's production
    return 'production';
  };

  const environment = getEnvironment();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const extractedOrderId = params.get("order_id");
    
    if (extractedOrderId) {
      setOrderId(extractedOrderId);
    }

    // Get user_id from localStorage
    const storedInfo = localStorage.getItem("upskill_user_info");
    let userId = null;
    if (storedInfo !== null) {
      const userInfo = JSON.parse(storedInfo);
      userId = userInfo.id;
    }

    // Verify payment with API
    if (extractedOrderId && userId) {
      const body = {
        task: "shurjopayVerifyPayment",
        order_id: extractedOrderId,
        environment: environment,
        user_id: userId
      };

      const headers = { "Content-Type": "application/json" };
      axios
        .post(Api.BKASH_API, body, { headers })
        .then((response) => {
          const status = response.data;
          
          if (status === 'successful') {
            // This shouldn't happen on failure page, but handle it
            setVerificationMessage('Payment processed.');
          } else if (status === 'failure') {
            setVerificationMessage('Payment failed, probably from your financial institution.');
          } else {
            setVerificationMessage('Unable to verify payment status.');
          }
        })
        .catch((error) => {
          console.error("Error verifying payment:", error);
          setVerificationMessage('Payment failed, probably from your financial institution.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // If no order_id or user_id, set default message
      setVerificationMessage('Payment failed, probably from your financial institution.');
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Processing payment result...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-4 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-md w-full text-center">
        {/* Status Icon */}
        <div className="mb-6 flex justify-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
            <XCircle className="w-12 h-12 text-red-600" />
          </div>
        </div>

        {/* Status Message */}
        <h2 className="text-3xl font-bold mb-2">
          <span className="text-red-600">Payment Failed</span>
        </h2>

        {verificationMessage ? (
          <p className="text-red-700 mb-8">
            {verificationMessage}
          </p>
        ) : (
          <p className="text-gray-600 mb-8">
            There was an issue processing your payment. Please try again.
          </p>
        )}

        {orderId && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Order ID:</p>
            <p className="text-lg font-semibold text-gray-800">{orderId}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => navigate("/payment")}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate("/landing")}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Go to Home
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Powered by ShurjoPay • Secure Payment Gateway
          </p>
        </div>
      </div>
    </div>
  );
}
